import React, { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input, Textarea, CustomSelect, Label, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { createRequirement, updateRequirement } from '../services/api';
import { useRequirementStore } from '../stores/pmRequirementStore';
import { saveOptimistic } from '../stores/createProjectItemStore';

import { PMRequirement } from '../types/pm';
import { getPriorityOptions, getRequirementStatusOptions } from '../utils/constants';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface RequirementDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  requirement?: PMRequirement | null;
}

interface FormData {
  title: string;
  description?: string;
  priority: string;
  category?: string;
  estimatedHours?: number;
  actualHours?: number;
  status?: string;
}

const RequirementDialog: React.FC<RequirementDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  requirement,
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const { t } = useTranslation(['translation', 'pages']);
  const numberInput = useNumberInput('decimal');

  const isEditMode = !!requirement;
  const watchedPriority = watch('priority', 'MEDIUM');
  const watchedStatus = watch('status', 'PENDING');

  const actualHandleClose = () => { reset(); onClose(false); };
  const { handleClose: handleCloseWithWarning, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      if (requirement) {
        // 編輯模式：使用 reset 設置初始值，避免標記為 dirty
        reset({
          title: requirement.title,
          description: requirement.description || '',
          priority: requirement.priority,
          category: requirement.category || '',
          estimatedHours: requirement.estimatedHours || undefined,
          actualHours: requirement.actualHours || undefined,
          status: requirement.status,
        });
      } else {
        // 新增模式：使用 reset 設置預設值
        reset({
          title: '',
          description: '',
          priority: 'MEDIUM',
          category: '',
          estimatedHours: undefined,
          actualHours: undefined,
          status: 'PENDING',
        });
      }
    }
  }, [isOpen, requirement, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: any = {
      title: data.title.trim(),
      description: data.description?.trim() || null,
      priority: data.priority,
      category: data.category?.trim() || null,
      estimatedHours: data.estimatedHours ? Number(data.estimatedHours) : null,
    };

    if (isEditMode) {
      payload.actualHours = data.actualHours ? Number(data.actualHours) : null;
      payload.status = data.status;
    }
    const now = new Date().toISOString();
    const draft: PMRequirement = isEditMode
      ? { ...requirement, ...payload }
      : { id: `temp-${Date.now()}`, projectId, status: 'PENDING', createdAt: now, updatedAt: now, ...payload };
    onClose(false);

    try {
      await saveOptimistic(useRequirementStore, {
        original: requirement,
        draft,
        request: () => (isEditMode ? updateRequirement(requirement.id, payload) : createRequirement(projectId, payload)),
      });
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
    } catch (error: any) {
      toast.error(error.response?.data?.error || t(isEditMode ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmRequirements.editTitle', { ns: 'pages' }) || 'Edit Requirement' : t('pmRequirements.addTitle', { ns: 'pages' }) || 'Add Requirement'} onClose={handleCloseWithWarning} closeLabel={t('common.actions.close')} />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {/* Title */}
            <FormFieldWithError
              label={t('pmRequirements.fields.title', { ns: 'pages' }) || 'Title'}
              required={true}
              error={errors.title?.message}
              htmlFor="title"
            >
              <Input
                id="title"
                {...register('title', {
                  required: t('pmRequirements.validation.titleRequired', { ns: 'pages' }) || 'Title is required',
                  maxLength: {
                    value: 200,
                    message: t('pmRequirements.validation.titleMaxLength', { ns: 'pages' }) || 'Title must not exceed 200 characters',
                  },
                })}
                variant={errors.title ? 'error' : 'default'}
                placeholder={t('pmRequirements.placeholders.title', { ns: 'pages' }) || 'Enter requirement title'}
              />
            </FormFieldWithError>

            {/* Description */}
            <FormFieldWithError
              label={t('pmRequirements.fields.description', { ns: 'pages' }) || 'Description'}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                {...register('description', {
                  maxLength: {
                    value: 2000,
                    message: t('pmRequirements.validation.descriptionMaxLength', { ns: 'pages' }) || 'Description must not exceed 2000 characters',
                  },
                })}
                variant={errors.description ? 'error' : 'default'}
                placeholder={t('pmRequirements.placeholders.description', { ns: 'pages' }) || 'Enter requirement description'}
                rows={3}
              />
            </FormFieldWithError>

            {/* Priority / Estimated Hours / Category Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="priority">{t('pmRequirements.fields.priority', { ns: 'pages' }) || 'Priority'}</Label>
                <CustomSelect
                  id="priority"
                  {...register('priority')}
                  value={watchedPriority}
                  onChange={(value) => setValue('priority', value)}
                  options={getPriorityOptions(t, 'pmRequirements')}
                />
              </div>

              <FormFieldWithError
                label={t('pmRequirements.fields.estimatedHoursWithUnit', { ns: 'pages' }) || 'Estimated Hours (hrs)'}
                error={errors.estimatedHours?.message}
                htmlFor="estimatedHours"
              >
                <Input
                  id="estimatedHours"
                  type="text"
                  {...numberInput.bind(register('estimatedHours', numberInput.validationRules))}
                  variant={errors.estimatedHours ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmRequirements.fields.category', { ns: 'pages' }) || 'Category'}
                error={errors.category?.message}
                htmlFor="category"
              >
                <Input
                  id="category"
                  {...register('category', {
                    maxLength: {
                      value: 100,
                      message: t('pmRequirements.validation.categoryMaxLength', { ns: 'pages' }) || 'Category must not exceed 100 characters',
                    },
                  })}
                  variant={errors.category ? 'error' : 'default'}
                  placeholder={t('pmRequirements.placeholders.category', { ns: 'pages' }) || 'e.g., Feature, UI, Performance'}
                />
              </FormFieldWithError>
            </div>

            {/* Actual Hours (Edit Mode Only) */}
            {isEditMode && (
              <FormFieldWithError
                label={t('pmRequirements.fields.actualHours', { ns: 'pages' }) || 'Actual Hours'}
                error={errors.actualHours?.message}
                htmlFor="actualHours"
              >
                <Input
                  id="actualHours"
                  type="text"
                  {...numberInput.bind(register('actualHours', numberInput.validationRules))}
                  variant={errors.actualHours ? 'error' : 'default'}
                  placeholder="0"
                />
              </FormFieldWithError>
            )}

            {/* Status (Edit Mode Only) */}
            {isEditMode && (
              <div>
                <Label htmlFor="status">{t('pmRequirements.fields.status', { ns: 'pages' }) || 'Status'}<span className="text-fg ml-1">*</span></Label>
                <CustomSelect
                  id="status"
                  {...register('status', {
                    required: t('pmRequirements.validation.statusRequired', { ns: 'pages' }) || 'Status is required',
                  })}
                  value={watchedStatus}
                  onChange={(value) => setValue('status', value)}
                  options={getRequirementStatusOptions(t)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className={dialogButtonClass.primary}
            >
              {isEditMode
                ? t('common.actions.save') || 'Save'
                : t('common.actions.create') || 'Create'}
            </button>
          </DialogFooter>
        </form>
    </DialogShell>

      <ConfirmModal isOpen={showConfirm} onClose={handleCancelClose} onConfirm={handleConfirmClose} title={t('components.modal.unsavedChanges.title')} message={t('components.modal.unsavedChanges.message')} confirmText={t('components.modal.unsavedChanges.confirmButton')} cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t} />
    </>
  );
};

export default RequirementDialog;
